<script setup>
import { computed, nextTick, ref, toRef } from 'vue';
import {
  Avatar,
  Button,
  Card,
  Chip,
  MenuButtonGroup,
  Menu,
  ProgressSpinner,
  Divider,
  Formatters,
} from '@dashhudson/dashing-ui';
import { getTimestampString } from '@/app/community/utils/formatter';
import AssignedTagGroup from '@/app/community/components/AssignedTagGroup.vue';
import AssignedTicketGroup from '@/app/community/components/AssignedTicketGroup.vue';
import InteractionUtility from '@/app/community/components/InteractionUtility.vue';
import ReplyComposer from '@/app/community/components/ReplyComposer.vue';
import {
  COMMUNITY_INTERACTION_TYPES,
  TWITTER_TWEET_TYPES,
  COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATIONS,
} from '@/app/community/constants';
import { attachmentFormatter, commentFormatter } from '@/app/community/utils/comments';
import AttachmentGroup from '@/app/community/components/AttachmentGroup.vue';
import NestedTweetPreview from '@/app/community/components/NestedTweetPreview.vue';
import {
  getUgcCommentSourceLink,
  getCommentSourceLink,
  getCommentSourceLinkTooltip,
  formatCount,
} from '@/app/community/utils/comment-detail';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { getUserInitials } from '@/utils/user';
import { useInteractionMenu } from '@/app/community/composables/useInteractionMenu';
import MessageText from '@/app/community/components/MessageText.vue';
import useRouteQuery from '@/app/community/composables/useRouteQuery';
import CommentInternalMessages from '@/app/community/components/CommentInternalMessages.vue';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { getUserTimezone } from '@/utils/timezone';

const props = defineProps({
  media: { type: Object, default: undefined },
  comment: { type: Object, default: undefined },
  root: { type: Boolean, default: undefined },
  ugc: { type: Boolean, default: undefined },
  activeItem: { type: Object, default: undefined },
  brand: { type: Object, default: undefined },
  hasActiveItem: { type: Boolean, default: undefined },
});

const emit = defineEmits('scrollToNewReply');
const multiBrandStore = useCommunityMultiBrandsStore();

const {
  commentButtonMenu,
  commentHiddenMenu,
  translatedText,
  translated,
  translating,
  showRepostTweetMenu,
  repostNestedMenuItems,
} = useInteractionMenu(toRef(props.comment));
const { isInternalNoteRoute, searchByAuthor } = useRouteQuery();

const expanded = ref(false);
const nestedContainer = ref(null);

const formatted = computed(() => commentFormatter(props.comment, props.brand));
const active = computed(() => props.activeItem?.id === props.comment?.id);
const supportive = computed(() => props.root || active.value);

const itemClass = computed(() => {
  const classList = [];
  if (active.value) {
    classList.push('dash-p-6 dash-bg-background-page');
  } else {
    classList.push('bg-transparent');
  }
  if (!supportive.value) {
    classList.push('ml-6');
  }
  return classList;
});
const severity = computed(() => (active.value ? 'active' : 'flat'));

const brandAccessibleUsers = computed(
  () => multiBrandStore.brandIdToAccessibleBrandUsersMap[props.brand?.id] ?? [],
);
const brandUserAuthor = computed(() => {
  const { brandUserReply } = props.comment ?? {};
  if (brandUserReply && brandAccessibleUsers.value.length) {
    return brandAccessibleUsers.value.find((u) => u.id === brandUserReply.userId);
  }
  return undefined;
});
const authorAvatar = computed(() => {
  const avatar = formatted.value.userAvatar;
  if (props.comment?.isOwned) {
    const { avatarUrl } = brandUserAuthor.value ?? {};
    return brandUserAuthor.value
      ? { image: avatarUrl, label: avatarUrl ? undefined : getUserInitials(brandUserAuthor.value) }
      : { image: avatar ?? props.brand?.avatarUrl };
  }
  return { image: formatted.value.userAvatar };
});
const authorName = computed(() => {
  const brandName = props.comment?.isOwned ? props.brand?.name : undefined;
  return formatted.value.username ?? brandName;
});
const timestamp = computed(() => getTimestampString(formatted.value.timestamp));
const timestampTooltip = computed(() =>
  Formatters.formatDate(new Date(props.comment.sourceUpdatedAt), {
    includeTime: true,
    timeZone: getUserTimezone(),
  }),
);
const commentMenu = computed(() => {
  if (props.comment.type === COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT_MENTION) {
    return {};
  }
  const { menuItems, numMenuButtons } = commentButtonMenu.value;
  return {
    model: active.value ? menuItems : [...menuItems, ...commentHiddenMenu.value],
    displayLimit: numMenuButtons,
  };
});
const unavailable = computed(() => {
  if (formatted.value.type === COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT) {
    return props.media?.status === 1 || formatted.value.isDeleted;
  }
  return formatted.value.isDeleted || formatted.value.isHidden;
});

const chipLabel = computed(() => {
  if (formatted.value.isDeleted) {
    return 'Deleted';
  }
  if (formatted.value.isHidden) {
    return 'Hidden';
  }
  return '';
});

const attachments = computed(() => {
  if (
    formatted.value.type !== COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET &&
    formatted.value?.attachments?.length
  ) {
    return attachmentFormatter(formatted.value.attachments);
  }
  return [];
});
const nestedTweet = computed(() => {
  const quotedTweet = formatted.value?.quotedStatus;
  const retweetedTweet = formatted.value?.retweetedStatus;
  return props.ugc ? (quotedTweet ?? retweetedTweet) : undefined;
});
const preview = computed(() => {
  if (
    nestedTweet.value &&
    (nestedTweet.value.tweetId ?? nestedTweet.value.twitterTweetId) !==
      props.media?.sourceData?.tweetId
  ) {
    const nestedAttachments = nestedTweet.value.attachments;
    return {
      attachments: nestedAttachments,
      avatar: nestedTweet.value.profilePictureUrl,
      authorName: nestedTweet.value.screenName,
      text: nestedTweet.value.text,
    };
  }
  return {
    attachments: [],
    avatar: props.brand?.avatarUrl,
    authorName: props.brand?.name,
    text: props.media?.sourceData.text,
  };
});
const canShowClickableLinkText = computed(() =>
  [
    ...TWITTER_TWEET_TYPES,
    COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT,
    COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT,
  ].includes(props.comment.type),
);
const commentText = computed(() => {
  if (translated.value) {
    return translatedText.value;
  }
  return props.comment.type === COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET
    ? undefined
    : formatted.value.text;
});
const directLink = computed(() => {
  if (props.ugc) {
    return getUgcCommentSourceLink(props.media, props.comment);
  }
  return getCommentSourceLink(formatted.value, props.media, props.activeItem);
});
const directLinkTooltip = computed(() => getCommentSourceLinkTooltip(formatted.value, props.ugc));
const canSearchAuthor = computed(() => !props.comment?.isOwned && !isInternalNoteRoute.value);

function onLinkClicked() {
  if (directLink.value) {
    window.open(directLink.value, '_blank');
  }
}

/**
 * Expand/Collapse Reply Thread
 */
const showThreadButton = computed(() => props.root && props.comment?.replies?.length > 1);
const hideCommentThread = computed(
  () => props.root && active.value && showThreadButton.value && !expanded.value,
);
const showReply = computed(() => expanded.value || props.comment?.replies?.length === 1);
const threadButtonLabel = computed(() => {
  if (showThreadButton.value) {
    if (expanded.value) {
      return 'Collapse';
    }
    const replyLength = props.comment.replies.length;
    const prefix = props.root && active.value ? '' : 'Other ';
    const count = props.root && active.value ? replyLength : replyLength - 1;
    const singularSuffix = 'Reply';
    const pluralSuffix = `${prefix}Replies`;
    const formattedCount = formatCount(count, singularSuffix, pluralSuffix);
    return `See ${formattedCount}`;
  }
  return undefined;
});

function toggleReplyThread() {
  expanded.value = !expanded.value;
}

function onAuthorNameClicked() {
  if (canSearchAuthor.value) {
    searchByAuthor(authorName.value);
  }
}

function handleNewReply() {
  expanded.value = true;
  nextTick(() => {
    if (nestedContainer.value) {
      emit('scrollToNewReply');
    }
  });
}
</script>

<template>
  <Card
    :severity="severity"
    :class="itemClass"
    :data-comment-active="active ? 'Active' : 'Inactive'"
    :data-comment-level="root ? 'Root' : 'Nested'"
    data-test="CommentItem"
  >
    <div class="dash-flex dash-flex-col dash-text-base dash-gap-4">
      <div
        :class="[
          'dash-flex dash-flex-col',
          { 'dash-gap-4': supportive, 'dash-gap-1': !supportive },
        ]"
      >
        <div
          class="dash-flex dash-items-center dash-justify-between"
          :class="{ '!py-1': !supportive }"
        >
          <div
            :class="[
              'dash-flex dash-items-center',
              { 'dash-gap-1': supportive, 'dash-gap-2': !supportive },
            ]"
          >
            <div
              v-dash-tooltip="canSearchAuthor ? 'Search all messages from user' : undefined"
              :class="[
                'dash-flex dash-items-center dash-gap-2',
                { 'dash-cursor-pointer': canSearchAuthor },
              ]"
            >
              <Avatar v-bind="authorAvatar" :size="supportive ? 'md' : 'sm'" />
              <div
                :class="[
                  'dash-flex dash-items-center',
                  { 'dash-gap-1': supportive, 'dash-gap-2': !supportive },
                ]"
              >
                <div
                  data-test="author-name"
                  :class="[
                    { 'dash-typo-base-medium': supportive },
                    { 'dash-typo-base': !supportive },
                  ]"
                  @click="onAuthorNameClicked"
                >
                  {{ authorName }}
                  <span v-if="comment?.isOwned" class="dash-text-subtle">(You)</span>
                </div>
              </div>
            </div>
            <div
              :class="[
                'dash-flex dash-items-center',
                { 'dash-gap-1': supportive, 'dash-gap-2': !supportive },
              ]"
            >
              <Chip
                v-if="unavailable"
                :label="chipLabel"
                size="sm"
                data-test="unavailable-chip"
                :severity="formatted.isDeleted || formatted.isHidden ? 'danger' : 'neutral'"
              />
              <Chip
                v-if="comment?.isAuthorBlocked"
                label="Blocked"
                severity="danger"
                size="sm"
                data-test="blocked-chip"
              />
              <Button
                v-if="supportive && directLink"
                v-dash-tooltip="directLinkTooltip"
                icon="link-external"
                size="xs"
                severity="tertiary"
                @click="onLinkClicked"
              />
              <span v-dash-tooltip="timestampTooltip" class="dash-text-subtle">{{
                timestamp
              }}</span>
            </div>
          </div>
          <div v-if="commentMenu.model?.length" data-test="CommentButtonGroup">
            <MenuButtonGroup
              key="commentItemMenuButtonGroup"
              v-bind="commentMenu"
              :disabled="formatted.isDeleted"
            />
          </div>
        </div>
        <div v-if="translating" class="dash-flex dash-items-center dash-gap-1">
          <ProgressSpinner :size="16" />
          <div class="dash-text-subtle">Translating ...</div>
        </div>
        <div v-else-if="commentText" :class="{ 'dash-text-subtle': !active }">
          <MessageText v-if="canShowClickableLinkText" :text="commentText" />
          <p v-else class="!whitespace-pre-line">{{ commentText }}</p>
        </div>
      </div>
      <NestedTweetPreview
        v-if="root && nestedTweet"
        v-bind="preview"
        :class="{ 'dash-border dash-border-bd-light': !active }"
      />
      <AttachmentGroup :attachments="attachments" :class="{ 'dash-opacity-30': !active }" />
      <AssignedTagGroup v-if="active" />
      <AssignedTicketGroup v-if="active" />
      <div class="dash-flex dash-items-center dash-gap-2" data-test="ActiveCommentUtility">
        <InteractionUtility
          v-if="active"
          :interaction="activeItem"
          :hidden-menu-items="commentHiddenMenu"
          :location="COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATIONS.PANEL"
        />
      </div>
      <!-- recursive components for nested replies of active comment -->
      <div
        v-if="comment.replies?.length && !hideCommentThread"
        ref="nestedContainer"
        class="dash-flex dash-flex-col dash-text-base dash-gap-4"
      >
        <template v-for="reply in comment.replies" :key="reply.id">
          <template v-if="reply.id === activeItem?.id || showReply">
            <CommentItem
              :comment="reply"
              :active-item="activeItem"
              :brand="brand"
              :has-active-item="hasActiveItem"
            />
          </template>
        </template>
      </div>
      <div v-else-if="!hasActiveItem">
        <CommentItem
          :comment="activeItem"
          :active-item="activeItem"
          :brand="brand"
          has-active-item
        />
      </div>
      <div v-if="showThreadButton" class="dash-flex">
        <Divider class="!mr-2.5 !w-[1.95rem]" />
        <Button
          :label="threadButtonLabel"
          text
          severity="secondary"
          data-test="ReplyThreadButton"
          @click="toggleReplyThread"
        />
      </div>
      <CommentInternalMessages
        v-if="supportive && active"
        :comment="comment"
        data-test="CommentInternalMessages"
      />
      <ReplyComposer
        v-if="active"
        :interaction="activeItem"
        :disabled="!!activeItem?.deletedAt"
        @expand-on-reply="handleNewReply"
      />
    </div>
    <Menu
      v-if="showRepostTweetMenu"
      variant="oneColumn"
      :model="repostNestedMenuItems"
      :dropdown="{
        distance: 0,
        targetNodes: `#repost-menu-${comment.id}`,
        referenceNode: `#repost-menu-${comment.id}`,
      }"
    />
  </Card>
</template>
